import { Controller } from "@hotwired/stimulus"
import { useClickOutside } from "stimulus-use"

// Connects to data-controller="components--calendar"
export default class extends Controller {
  static targets = [
    "calendarCurrentMonth",
    "calendarCurrentYear",
    "calendarDay",
    "calendarDaysContainer",
    "calendarDays",
    "templateInactiveDay",
    "templateDay",
    "templateDays",
    "templateMonth",
    "templateYear",
    "calendarDaysHeader",
    "calendarMonthsContainer",
    "calendarMonths",
    "calendarYearsContainer",
    "calendarYears",
  ]

  static values = {
    initialDate: String, // Initiales Datum im Format YYYY-M-D
    selectedDate: String, // Ausgewähltes Datum im Format YYYY-M-D
    day: Number,
    days: Array, // Array der Wochentage
    month: Number,
    months: Array, // Array der Monatsnamen
    monthsAbbreviation: Array, // Array der Monatsnamen, abgekürzt
    year: Number,
    showYears: Number, // Anzahl Jahre in Auswahl Jahre
    dateFieldId: String, // ID des Datumsfeldes
    enableFutureDates: Boolean, // Erlaube zukünftiges Datum
    enablePastDates: Boolean, // Erlaube vergangenes Datum
    enableMonthsAbbreviation: Boolean, // Erlaube Monatsabkürzungen
    enableCloseAfterSelect: Boolean, // Erlaube Schließen nach Auswahl
  }

  static classes = [
    "dateToday",
    "selectedDate",
    "selectedDateToday",
  ]

  connect() {
    this.init()
    useClickOutside(this)
  }

  clickOutside(event) {
    this.hideCalendar()
  }

  checkRequirements() {
    if (this.dateFieldIdValue === undefined || this.dateFieldIdValue === "") {
      console.error("components__calendar_date_field_id_value -> ID für das Datumsfeld wurde nicht angegeben!")
      this.element.innerHTML = "components__calendar_date_field_id_value -> ID für das Datumsfeld wurde nicht angegeben!"
      return
    }
  }

  init() {
    this.checkRequirements()

    this.dateFieldTarget = document.getElementById(this.dateFieldIdValue)

    this.createCalendarDaysHeader()
    this.renderCalendarDays()
  }

  forward() {
    if (!this.calendarDaysContainerTarget.classList.contains("hidden")) { this.gotoNextMonth() }

    if (!this.calendarMonthsContainerTarget.classList.contains("hidden")) {
      this.gotoForwardYear()
      this.renderCalendarMonths()
    }

    if (!this.calendarYearsContainerTarget.classList.contains("hidden")) {
      this.gotoForwardYear()
      this.renderCalendarYears()
    }
  }

  backward() {
    if (!this.calendarDaysContainerTarget.classList.contains("hidden")) { this.gotoPreviousMonth() }

    if (!this.calendarMonthsContainerTarget.classList.contains("hidden")) {
      this.gotoBackwardYear()
      this.renderCalendarMonths()
    }

    if (!this.calendarYearsContainerTarget.classList.contains("hidden")) {
      this.gotoBackwardYear()
      this.renderCalendarYears()
    }
  }

  // Ausgewähltes Datum
  selectDate(event) {
    this.calendarDayTargets.forEach(day => {
      if (day.classList.contains(...this.selectedDateClasses)) {
        day.classList.remove(...this.selectedDateClasses)
        day.querySelector("span").classList.remove(...this.selectedDateTodayClasses)
      }
    })

    event.currentTarget.classList.add(...this.selectedDateClasses)

    if (this.initialDateValue === event.currentTarget.dataset.date) {
      event.currentTarget.querySelector("span").classList.add(...this.selectedDateTodayClasses)
    }

    this.selectedDateValue = event.currentTarget.dataset.date

    this.dateFieldTarget.value = [
      event.currentTarget.dataset.date.split("-")[2].padStart(2, '0'),
      event.currentTarget.dataset.date.split("-")[1].padStart(2, '0'),
      event.currentTarget.dataset.date.split("-")[0]
    ].join(".")

    if (this.enableCloseAfterSelectValue) { this.hideCalendar() }
  }

  // Setze Kalender zurück auf aktuelles Datum
  today() {
    this.calendarDaysContainerTarget.classList.remove("hidden")
    this.calendarMonthsContainerTarget.classList.add("hidden")
    this.calendarYearsContainerTarget.classList.add("hidden")

    this.monthValue = this.initialDateValue.split("-")[1]
    this.yearValue = this.initialDateValue.split("-")[0]

    this.renderCalendarDays()
  }

  hideCalendar() {
    this.element.classList.add("hidden")
  }

  toggleMonths(event) {
    if (this.calendarMonthsContainerTarget.classList.contains("hidden")) {
      this.calendarDaysContainerTarget.classList.add("hidden")
      this.calendarYearsContainerTarget.classList.add("hidden")
      this.calendarMonthsContainerTarget.classList.remove("hidden")

      this.renderCalendarMonths()
    } else {
      this.calendarDaysContainerTarget.classList.remove("hidden")
      this.calendarYearsContainerTarget.classList.add("hidden")
      this.calendarMonthsContainerTarget.classList.add("hidden")
    }
  }

  // Render Monatsansicht
  renderCalendarMonths() {
    this.calendarMonthsTarget.innerHTML = ""
    this.calendarMonths = []

    let templateMonth = this.templateMonthTarget.content

    for (let i = 0; i < 12; i++) {
      let previousDay = this.buildMonth(templateMonth.cloneNode(true), i, this.yearValue)

      this.calendarMonths.push(previousDay)
    }

    this.calendarMonthsTarget.append(...this.calendarMonths)
  }

  buildMonth(template, month, year) {
    let monthName = this.monthsAbbreviationValue[month]

    template.firstElementChild.dataset.month = month + 1
    template.firstElementChild.dataset.year = year
    template.firstElementChild.innerText = monthName

    return template.firstElementChild
  }

  selectMonth(event) {
    this.monthValue = event.currentTarget.dataset.month
    this.yearValue = event.currentTarget.dataset.year

    this.toggleMonths()
    this.renderCalendarDays()
  }

  selectYear(event) {
    this.yearValue = event.currentTarget.dataset.year

    this.toggleYears()
    this.renderCalendarDays()
  }

  toggleYears(event) {
    if (this.calendarYearsContainerTarget.classList.contains("hidden")) {
      this.calendarDaysContainerTarget.classList.add("hidden")
      this.calendarMonthsContainerTarget.classList.add("hidden")
      this.calendarYearsContainerTarget.classList.remove("hidden")

      this.renderCalendarYears()
    } else {
      this.calendarDaysContainerTarget.classList.remove("hidden")
      this.calendarMonthsContainerTarget.classList.add("hidden")
      this.calendarYearsContainerTarget.classList.add("hidden")
    }
  }

  // Render Jahresansicht
  renderCalendarYears() {
    this.calendarYearsTarget.innerHTML = ""
    this.calendarYears = []

    let templateYear = this.templateYearTarget.content

    for (let i = this.showYearsValue; i > 0; i--) {
      let year = this.buildYear(templateYear.cloneNode(true), this.yearValue - i)

      this.calendarYears.push(year)
    }

    for (let i = 0; i <= this.showYearsValue; i++) {
      let year = this.buildYear(templateYear.cloneNode(true), this.yearValue + i)

      this.calendarYears.push(year)
    }

    this.calendarYearsTarget.append(...this.calendarYears)
  }

  buildYear(template, year) {
    template.firstElementChild.dataset.year = year
    template.firstElementChild.innerText = year

    if (this.yearValue === year) {
      template.firstElementChild.classList.add("font-semibold")
    }

    return template.firstElementChild
  }

  // Gehe Jahr zurück
  gotoBackwardYear() {
    this.yearValue = this.yearValue - 1
    this.setDateLabel()
  }

  // Gehe Jahr vor
  gotoForwardYear() {
    this.yearValue = this.yearValue + 1
    this.setDateLabel()
  }

  // Gehe Monat zurück
  gotoPreviousMonth() {
    if (this.monthValue <= 1) {
      this.monthValue = 12
      this.yearValue = this.yearValue - 1
    } else {
      this.monthValue = this.monthValue - 1
    }

    this.renderCalendarDays()
  }

  // Gehe Monat vor
  gotoNextMonth() {
    if (this.monthValue >= 12) {
      this.monthValue = 1
      this.yearValue = this.yearValue + 1
    } else {
      this.monthValue = this.monthValue + 1
    }

    this.renderCalendarDays()
  }

  // Render Tagesansicht
  renderCalendarDays() {
    this.calendarDaysTarget.innerHTML = ""
    this.calendarDays = []

    let firstDay = new Date(this.yearValue, this.monthValue - 1, 0).getDay()
    let lastDate = new Date(this.yearValue, this.monthValue, 0).getDate()
    let dayEnd = new Date(this.yearValue, this.monthValue - 1, lastDate).getDay()
    let monthLastDate = new Date(this.yearValue, this.monthValue - 1, 0).getDate()

    let templateActive = this.templateDayTarget.content
    let templateInactive = this.templateInactiveDayTarget.content

    for (let i = firstDay; i > 0; i--) {
      let previousDay = this.buildDay(templateInactive.cloneNode(true), monthLastDate - i + 1, this.previousMonthFromCurrent['month'], this.previousMonthFromCurrent['year'])

      this.calendarDays.push(previousDay)
    }

    for (let i = 1; i <= lastDate; i++) {
      let day = this.buildDay(templateActive.cloneNode(true), i, this.monthValue, this.yearValue)

      this.calendarDays.push(day)
    }

    for (let i = dayEnd; i < 7; i++) {
      let nextDay = this.buildDay(templateInactive.cloneNode(true), i - dayEnd + 1, this.nextMonthFromCurrent['month'], this.nextMonthFromCurrent['year'])

      this.calendarDays.push(nextDay)
    }

    this.setDateLabel()
    this.calendarDaysTarget.append(...this.calendarDays)
  }

  buildDay(template, day, month, year) {
    let dateString = `${year}-${month}-${day}`

    template.firstElementChild.dataset.date = dateString
    template.firstElementChild.querySelector("span").innerText = day

    if (this.initialDateValue === dateString) {
      template.firstElementChild.querySelector("span").classList.add(...this.dateTodayClasses)
    }

    if (this.selectedDateValue === dateString) {
      template.firstElementChild.classList.add(...this.selectedDateClasses)
      template.firstElementChild.querySelector("span").classList.add(...this.selectedDateTodayClasses)
    }

    return template.firstElementChild
  }

  setDateLabel() {
    if (this.enableMonthsAbbreviationValue) {
      this.calendarCurrentMonthTarget.innerText = `${this.monthsAbbreviationValue[this.monthValue - 1]}`
    } else {
      this.calendarCurrentMonthTarget.innerText = `${this.monthsValue[this.monthValue - 1]}`
    }

    this.calendarCurrentYearTarget.innerText = `${this.yearValue}`
  }

  get previousMonthFromCurrent() {
    let month = this.monthValue - 1
    let year = this.yearValue

    if (month < 1) { month = 12; year = this.yearValue - 1 }

    return { month: month, year: year }
  }

  get nextMonthFromCurrent() {
    let month = this.monthValue + 1
    let year = this.yearValue

    if (month >= 12) { month = 1; year = this.yearValue + 1 }

    return { month: month, year: year }
  }

  createCalendarDaysHeader() {
    this.daysValue.forEach(day => {
      let div = this.templateDaysTarget.content.cloneNode(true)
      div.firstElementChild.innerText = day
      this.calendarDaysHeaderTarget.appendChild(div)
    })
  }
}
